import { FC, useEffect, useReducer } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { Box, Collapse, Divider, Flex, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { ScheduledBankTransferFrequency } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import BackButton from 'components/BackButton';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import * as routes from 'features/Bank/routes';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import { formatDate } from 'helpers/date';
import * as ContentLayout from 'layouts/ContentLayout';
import ErrorLayout from 'layouts/ErrorLayout';
import * as Layout from 'layouts/StepperLayout';

import { frequencyLocales } from '../../commonLocales';
import BankAccountsDetails from '../../components/BankAccountsDetails';
import useBankTransferData from '../../hooks/useBankTransferData';
import {
  getLastDueDateForDisplay,
  ScheduledBankTransferEndDateDeterminedByOption,
} from '../../utils';
import DuplicateTransfersModal from './components/DuplicateTransfersModal';
import {
  ConfirmDetailsFormSchema,
  confirmDetailsFormSchema,
} from './confirmDetailsForm.schema';
import { useCompanySubscriptionPlan } from './hooks/useCompanySubscriptionPlan';
import { useIsDuplicateTransfer } from './hooks/useIsDuplicateTransfer';
import locales from './locales';

const ConfirmExternalDetails: FC<StepProps<BankTransferCreationStep>> = (
  props,
) => {
  const { bankTransfer, setBankTransfer } = useBankTransferContext(
    'confirmExternalDetails',
  );
  const {
    amount,
    bankTransferRecipientId,
    fromBankAccountId,
    isInstant,
    label,
    schedule,
  } = bankTransfer;
  const {
    data: bankTransferData,
    error: bankTransferDataError,
    loading: bankTransferDataLoading,
    refetch: refetchBankTranferData,
  } = useBankTransferData({
    bankTransferRecipientId,
    fromBankAccountId,
  });
  const {
    data: subscriptionPlanData,
    error: subscriptionPlanDataError,
    loading: subscriptionPlanDataLoading,
    refetch: refetchSubscriptionPlan,
  } = useCompanySubscriptionPlan();

  const {
    error: searchBankTransfersError,
    isDuplicateTransfer,
    loading: searchBankTransfersLoading,
    refetch: refetchSearchBankTransfers,
  } = useIsDuplicateTransfer();

  const history = useHistory();

  const [openDuplicateTransfersModal, toggleDuplicateTransfersModal] =
    useReducer((state) => !state, false);

  const isLoading =
    (bankTransferDataLoading && !bankTransferData) ||
    (subscriptionPlanDataLoading && !subscriptionPlanData);
  searchBankTransfersLoading;

  const defaultRecipentEmail =
    bankTransferData?.viewer.company.bankTransferRecipient.email;

  const form = useForm<ConfirmDetailsFormSchema>({
    defaultValues: {
      recipientEmail: defaultRecipentEmail ?? '',
      shouldNotifyRecipient: false,
    },
    resolver: zodResolver(confirmDetailsFormSchema),
  });

  useEffect(() => {
    if (defaultRecipentEmail) {
      form.setValue('recipientEmail', defaultRecipentEmail);
    }
  }, [defaultRecipentEmail, form]);

  if (bankTransferDataError) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetchBankTranferData} />
      </ErrorLayout>
    );
  }

  if (subscriptionPlanDataError) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetchSubscriptionPlan} />
      </ErrorLayout>
    );
  }

  if (searchBankTransfersError) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetchSearchBankTransfers} />
      </ErrorLayout>
    );
  }

  if (isLoading || !bankTransferData || !subscriptionPlanData) {
    return <Loader />;
  }

  const {
    fromBankAccount,
    viewer: {
      company: { bankTransferRecipient },
    },
  } = bankTransferData;

  const {
    viewer: {
      company: { currentPlan, subscriptionPlan },
    },
  } = subscriptionPlanData;
  if (!subscriptionPlan) {
    throw new Error('No subscription plan found in bank transfer flow');
  }
  const {
    quotas: { sepaTransferMonthQuota },
  } = subscriptionPlan;

  const onSubmit = () => {
    if (isDuplicateTransfer) {
      toggleDuplicateTransfersModal();
      return;
    }

    props.goNext();
  };

  const fixedFeesDecimal = currentPlan.fees.additionalSepaTransfer.fixed / 100;

  const formattedFixedFees = formatAmount({
    forcePlusSign: false,
    value: fixedFeesDecimal,
  });

  const formattedCurrentFees = formatAmount({
    forcePlusSign: false,
    value: sepaTransferMonthQuota?.isExceeded ? fixedFeesDecimal : 0,
  });

  return (
    <>
      <Box
        as="form"
        height="100%"
        onSubmit={form.handleSubmit((values) => {
          setBankTransfer((prevState) => ({
            ...prevState,
            recipientEmail: values.recipientEmail,
            shouldNotifyRecipient: values.shouldNotifyRecipient,
          }));
          onSubmit();
        })}
      >
        <Layout.Root>
          <Layout.Body>
            <TrackView pageName="ConfirmNewExternalTransferDetails" />

            <Layout.HeaderButtonsContainer>
              <BackButton onClick={props.goBack} />
            </Layout.HeaderButtonsContainer>

            <Layout.BodyScrollableSection>
              <ContentLayout.Header title={locales.checkDetails} />

              <VStack
                alignItems="stretch"
                paddingBottom="space-32"
                spacing="space-48"
                width="100%"
              >
                <BankAccountsDetails
                  bankTransferRecipient={bankTransferRecipient}
                  emittingBankAccount={fromBankAccount}
                />

                <VStack alignItems="stretch" spacing="space-8">
                  <Text fontWeight="weight-500" size="lg" variant="primary">
                    {locales.transferRecap}
                  </Text>
                  <SunshineCard.Group>
                    <SunshineCard>
                      <SunshineCard.Content
                        flexDirection="row"
                        justifyContent="space-between"
                        spacing="space-24"
                      >
                        <Text>{locales.amount}</Text>
                        <Text fontWeight="weight-500" variant="primary">
                          {formatAmount({
                            forcePlusSign: false,
                            value: amount ?? 0,
                          })}
                        </Text>
                      </SunshineCard.Content>
                    </SunshineCard>
                    <SunshineCard>
                      <SunshineCard.Content
                        flexDirection="row"
                        justifyContent="space-between"
                        spacing="space-24"
                      >
                        <Text>{locales.fees}</Text>
                        <Flex flexDirection="row" gap="space-4">
                          {schedule ? (
                            <>
                              <Tooltip
                                description={
                                  locales.formatString(
                                    locales.scheduledFeeDetails,
                                    { formattedFixedFees },
                                  ) as string
                                }
                              >
                                <SunshineIcon
                                  color="grey.600"
                                  marginTop="2px"
                                  name="information"
                                />
                              </Tooltip>
                              <Text variant="primary">
                                {locales.scheduledFees}
                              </Text>
                            </>
                          ) : (
                            <>
                              <Tooltip
                                description={
                                  locales.formatString(
                                    sepaTransferMonthQuota?.isExceeded
                                      ? locales.feeDetailsQuotaReached
                                      : locales.feeDetails,
                                    {
                                      formattedFixedFees,
                                      nbFreeBankTransfersLeft: Math.max(
                                        (sepaTransferMonthQuota?.limit ?? 0) -
                                          (sepaTransferMonthQuota?.used ?? 0),
                                        0,
                                      ),
                                    },
                                  ) as string
                                }
                              >
                                <SunshineIcon
                                  color="grey.600"
                                  marginTop="2px"
                                  name="information"
                                />
                              </Tooltip>
                              <Text variant="primary">
                                {formattedCurrentFees}
                              </Text>
                            </>
                          )}
                        </Flex>
                      </SunshineCard.Content>
                    </SunshineCard>
                    <SunshineCard>
                      <SunshineCard.Content
                        flexDirection="row"
                        justifyContent="space-between"
                        spacing="space-24"
                        textAlign="right"
                      >
                        <Text>{locales.label}</Text>
                        {label ? (
                          <Text variant="primary">{label}</Text>
                        ) : (
                          <Text variant="disabled">{locales.noneLabel}</Text>
                        )}
                      </SunshineCard.Content>
                    </SunshineCard>
                    <SunshineCard>
                      <SunshineCard.Content
                        flexDirection="row"
                        justifyContent="space-between"
                        spacing="space-24"
                        textAlign="right"
                      >
                        <Text>{locales.transferType}</Text>
                        <Flex flexDirection="row" gap="space-4">
                          {isInstant ? (
                            <>
                              <SunshineIcon
                                color="blue.600"
                                marginTop="2px"
                                name="flash"
                              />
                              <Text variant="primary">
                                {locales.instantTransfer}
                              </Text>
                            </>
                          ) : (
                            <>
                              <Tooltip
                                description={locales.formatString(
                                  locales.standardTransferTooltip,
                                )}
                              >
                                <SunshineIcon
                                  color="grey.600"
                                  marginTop="2px"
                                  name="information"
                                />
                              </Tooltip>
                              <Text variant="primary">
                                {schedule
                                  ? locales.scheduledTransfer
                                  : locales.standardTransfer}
                              </Text>
                            </>
                          )}
                        </Flex>
                      </SunshineCard.Content>
                    </SunshineCard>
                  </SunshineCard.Group>
                </VStack>
                {schedule ? (
                  <VStack alignItems="stretch" spacing="space-8">
                    <Text fontWeight="weight-500" size="lg" variant="primary">
                      {locales.schedule}
                    </Text>
                    <SunshineCard.Group>
                      <SunshineCard>
                        <SunshineCard.Content
                          flexDirection="row"
                          justifyContent="space-between"
                          spacing="space-24"
                        >
                          <Text>{locales.firstOccurrence}</Text>
                          <Text variant="primary">
                            {formatDate(new Date(schedule.nextDueDate), 'P')}
                          </Text>
                        </SunshineCard.Content>
                      </SunshineCard>
                      <SunshineCard>
                        <SunshineCard.Content
                          flexDirection="row"
                          justifyContent="space-between"
                        >
                          <Text>{locales.frequency}</Text>

                          <Text variant="primary">
                            {frequencyLocales[schedule.frequency]}
                          </Text>
                        </SunshineCard.Content>
                      </SunshineCard>
                      {schedule.frequency !==
                      ScheduledBankTransferFrequency.Once ? (
                        <SunshineCard>
                          <SunshineCard.Content
                            flexDirection="row"
                            justifyContent="space-between"
                            spacing="space-24"
                          >
                            <Text>{locales.lastOccurrence}</Text>
                            <Text variant="primary">
                              {getLastDueDateForDisplay({
                                ...schedule,
                                endDateDeterminedBy:
                                  schedule.endDateDeterminedBy ??
                                  ScheduledBankTransferEndDateDeterminedByOption.Never,
                                numberOfOccurrences:
                                  schedule.remainingNumberOfOccurrences,
                              })}
                            </Text>
                          </SunshineCard.Content>
                        </SunshineCard>
                      ) : null}
                    </SunshineCard.Group>
                  </VStack>
                ) : null}

                <VStack alignItems="stretch" spacing="space-24">
                  <Text fontWeight="weight-500" size="lg" variant="primary">
                    {locales.notifyBeneficiary}
                  </Text>
                  <Controller
                    control={form.control}
                    name="shouldNotifyRecipient"
                    render={({ field }) => (
                      <Form.Checkbox
                        isChecked={field.value}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                      >
                        <Form.Checkbox.Title>
                          {locales.shouldNotifyRecipient}
                        </Form.Checkbox.Title>
                      </Form.Checkbox>
                    )}
                  />

                  <Collapse in={form.watch('shouldNotifyRecipient')}>
                    <Form.Field
                      error={form.formState.errors.recipientEmail?.message}
                      hint={locales.emailConfirmationDetail}
                      label={locales.email}
                    >
                      <Form.Input {...form.register('recipientEmail')} />
                    </Form.Field>
                  </Collapse>
                </VStack>
              </VStack>
            </Layout.BodyScrollableSection>

            <Layout.FooterContainer>
              <Divider />
              <Layout.FooterButtonsContainer>
                <Button
                  icon="arrow-right"
                  iconPosition="right"
                  isLoading={searchBankTransfersLoading}
                  type="submit"
                >
                  {locales.confirm}
                </Button>
              </Layout.FooterButtonsContainer>
            </Layout.FooterContainer>
          </Layout.Body>
        </Layout.Root>
      </Box>
      {isDuplicateTransfer ? (
        <DuplicateTransfersModal
          isOpen={openDuplicateTransfersModal}
          onCancel={() => history.push(routes.bankOverview)}
          onClose={toggleDuplicateTransfersModal}
          onConfirm={props.goNext}
        />
      ) : null}
    </>
  );
};

export default ConfirmExternalDetails;
